import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { Copy, Trash2, MoreHorizontal, Check, Loader2, Download, Printer, Tag } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
  DataTableToolbar,
  TableSearch,
  SegmentedTabs,
  TableCount,
  CopyRefChip,
  DataTablePagination,
} from 'components/ui/data-table';
import { StatusBadge, type StatusTone } from 'components/ui/status-badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'components/ui/dropdown-menu';
import EmptyState from 'components/ui/EmptyState';
import {
  accessKeyStatus,
  exportAccessKeysCsv,
  printAccessKeysSheet,
  type AccessKeyRow,
  type AccessKeyStatus,
  type AccessKeyColumnLabels,
} from './exportAccessKeys';

const STATUS_TONE: Record<AccessKeyStatus, StatusTone> = {
  unused: 'success',
  used: 'info',
  expired: 'neutral',
};

interface AccessKeysTableProps {
  rows: AccessKeyRow[];
  isLoading: boolean;
  onDelete: (id: string) => void;
  deletingId?: string | null;
}

/** Export CSV and Print run client-side over the loaded per-test invite codes. */
export const AccessKeysTable: React.FC<AccessKeysTableProps> = ({ rows, isLoading, onDelete, deletingId }) => {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { input: search, setInput: setSearch } = useSearchParamState();
  const [statusTab, setStatusTab] = useState<'all' | AccessKeyStatus>('all');
  const { page, pageSize, setPage, setPageSize } = usePaginationParams({
    defaultPage: 1,
    defaultPageSize: 10,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusTab !== 'all' && accessKeyStatus(row) !== statusTab) return false;
      if (q && !row.code.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, statusTab]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Summary counts across ALL codes (not the filtered view) for the header line.
  const counts = useMemo(() => {
    let used = 0;
    let unused = 0;
    let expired = 0;
    rows.forEach((row) => {
      const status = accessKeyStatus(row);
      if (status === 'used') used += 1;
      else if (status === 'expired') expired += 1;
      else unused += 1;
    });
    return { total: rows.length, used, unused, expired };
  }, [rows]);

  // Snap back to a valid page whenever the filtered set shrinks.
  useEffect(() => {
    setPage(1);
  }, [search, statusTab, pageSize, setPage]);

  const columnLabels: AccessKeyColumnLabels = {
    code: t('code'),
    status: t('status'),
    usedBy: t('used_by_2'),
    usedAt: t('used_at'),
    expires: t('expires'),
    statusText: {
      unused: t('status_unused'),
      used: t('status_used'),
      expired: t('status_expired'),
    },
  };

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };
  const formatDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="py-8">
        <EmptyState icon={<Tag size={26} />} title={t('no_invite_codes_for_this_test_yet')} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h3 className="text-sm font-semibold text-foreground">{t('access_generated_codes')}</h3>
        <span className="text-xs text-muted-foreground">
          {t('access_codes_summary', { total: counts.total, used: counts.used, unused: counts.unused })}
          {counts.expired > 0 && ` · ${t('access_codes_expired_count', { count: counts.expired })}`}
        </span>
      </div>

      <DataTableToolbar>
        <TableSearch
          value={search}
          onChange={setSearch}
          placeholder={t('search_by_code')}
          aria-label={t('search_by_code')}
        />
        <SegmentedTabs<'all' | AccessKeyStatus>
          aria-label={t('status')}
          value={statusTab}
          onChange={setStatusTab}
          options={[
            { value: 'all', label: t('all') },
            { value: 'unused', label: t('status_unused') },
            { value: 'used', label: t('status_used') },
            { value: 'expired', label: t('status_expired') },
          ]}
        />
        <TableCount>
          {filtered.length} {t('items')}
        </TableCount>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportAccessKeysCsv(filtered, columnLabels)}
            disabled={filtered.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            {t('access_export_csv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => printAccessKeysSheet(filtered, columnLabels, t('test_access'))}
            disabled={filtered.length === 0}
          >
            <Printer className="mr-2 h-4 w-4" />
            {t('access_print_sheet')}
          </Button>
        </div>
      </DataTableToolbar>

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">{t('code')}</TableHead>
              <TableHead className="whitespace-nowrap">{t('used_by_2')}</TableHead>
              <TableHead className="whitespace-nowrap">{t('used_at')}</TableHead>
              <TableHead className="whitespace-nowrap">{t('expires')}</TableHead>
              <TableHead className="whitespace-nowrap">{t('status')}</TableHead>
              <TableHead className="whitespace-nowrap text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((row) => {
              const status = accessKeyStatus(row);
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <CopyRefChip
                      value={row.code}
                      label={t('copy_code')}
                      copiedLabel={t('code_copied')}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {row.candidate?.name || row.candidate?.email || '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(row.usedAt)}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDate(row.expiresAt)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge tone={STATUS_TONE[status]}>{t(`status_${status}`)}</StatusBadge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => copyToClipboard(row.code, row.id)}>
                          {copiedId === row.id ? (
                            <Check className="mr-2 h-4 w-4 text-success-600" />
                          ) : (
                            <Copy className="mr-2 h-4 w-4" />
                          )}
                          {t('copy_code')}
                        </DropdownMenuItem>
                        {status === 'unused' && (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            disabled={deletingId === row.id}
                            onSelect={() => onDelete(row.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('delete')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {filtered.length > pageSize && (
        <DataTablePagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
};

export default AccessKeysTable;
